import React from "react";
import type { ApolloClient } from "@apollo/client";
import moment from "moment";
import { Card, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { Formik } from "formik";
import { find, get, isEmpty, map } from "lodash";
import { toast } from "react-toastify";
import { wrapError } from "utils/errorHelper";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import {
  FiCalendar,
  FiCheck,
  FiClock,
  FiEye,
  FiFileText,
} from "react-icons/fi";
import { getUser } from "actions/adminActions";
import { createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";

import { MdPriorityHigh } from "react-icons/md";
import { ScrollToFieldError } from "utils/form";
import {
  approveYellowPathAppeal,
  denyYellowPathAppeal,
  retryYellowPathAppeal,
} from "services/adminService";
import type { RootState } from "types/state";

import Button from "components/Button";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import SelfApprovalAlert from "pages/admin/SelfApprovalAlert";
import IconMessageCard from "components/IconMessageCard";
import AddressApproval from "./AddressApproval";
import NameChangeApproval from "./NameChangeApproval";
import YellowPathApproval from "./YellowPathApproval";
import UnfreezeRequestApproval from "./UnfreezeRequestApproval";
import BusinessMemberProfileUpdateApproval from "./BusinessMemberProfileUpdateApproval";
import BusinessRegistrationUpdateApproval from "./BusinessRegistrationUpdateApproval";
import UserKycProfileUpdateApproval from "./UserKycProfileUpdateApproval";
import Viewer from "react-viewer";
import PdfViewer from "components/PdfViewer";
import "./Approval.scss";

import * as yup from "yup";
const schema = yup.object({
  approvalStatus: yup.string().required(),
  reason: yup.string(),
  comments: yup.string(),
  appealReason: yup.string().when(["approvalStatus", "isYellowPath"], {
    is: (status: string, isYellowPath: boolean) =>
      status !== "RETRY" && isYellowPath === true,
    then: (s) => s.required("An appeal reason is required"),
    otherwise: (s) => s,
  }),
});

const approvalOptions = [
  { value: "APPROVED", label: "Approved" },
  { value: "DENIED", label: "Denied" },
  { value: "RETRY", label: "Request New Documents" },
];

/** A document attached to an approval request. */
interface ApprovalDocument {
  id: string;
  name: string;
  uri: string;
  contentType?: string;
  metadata?: { downloadUrl?: string };
}

/** The approval entity passed in by the parent (still loosely shaped via `get`). */
interface Approval {
  type?: { value?: string; label?: string };
  status?: string;
  updatedBy?: string;
  updatedAt?: string;
  createdAt?: string;
  createdBy?: string | null;
  entityIdentifier?: { id?: string; type?: string };
  documents: ApprovalDocument[];
  allReasons?: string[];
  metadataItems?: Array<{ name?: string; value?: string }>;
}

/** Result of the parent `onSubmit` callback — inspected for an `error` field. */
interface SubmitResult {
  success: boolean;
}

interface MetadataItem {
  name?: string;
  value?: string;
}

/** Formik values: initialized from `{ ...this.state, isYellowPath }`. */
interface ApprovalFormValues {
  approvalStatus: string;
  comments: string;
  reason: string;
  appealReason: string;
  documentToView: Partial<ApprovalDocument>;
  showDenyModal: boolean;
  pendingValues: ApprovalFormValues | null;
  showSuccessModal: boolean;
  isSubmitting: boolean;
  user: object;
  isFetching: boolean;
  isYellowPath?: boolean;
  metadata?: { appealReason?: string };
}

interface State {
  approvalStatus: string;
  comments: string;
  reason: string;
  appealReason: string;
  documentToView: Partial<ApprovalDocument>;
  showDenyModal: boolean;
  pendingValues: ApprovalFormValues | null;
  showSuccessModal: boolean;
  isSubmitting: boolean;
  user: object;
  isFetching: boolean;
}

const loadingSelector = createLoadingSelector([adminConstants.GET_USER]);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getUser,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onSubmit: (values: ApprovalFormValues) => Promise<SubmitResult>;
  error?: string;
  isSubmitting?: boolean;
  isSelfApproval?: boolean;
  approval: Approval;
  user?: object;
  onClose?: () => void;
  userId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

class ApprovalDocumentItem extends React.PureComponent<Props, State> {
  constructor() {
    // preserve original no-arg super() call; props are not read here
    super(undefined as unknown as Props);

    this.state = {
      approvalStatus: "",
      comments: "",
      reason: "NONE",
      appealReason: "",
      documentToView: {},
      showDenyModal: false,
      pendingValues: null,
      showSuccessModal: false,
      isSubmitting: false,
      user: {},
      isFetching: true,
    };
  }

  async componentDidMount() {
    await this._getAndSetUser();
    this.setState({
      isFetching: false,
    });
  }

  _getAndSetUser = async () => {
    const entity = get(this.props.approval, "entityIdentifier");
    const isEmployer = entity && entity.type === "Employer";

    if (!this.props.userId || isEmployer) return;

    // getUser dispatches a lifecycle action whose return type is a union of
    // success ({ type, data }) and failure ({ type, error }) shapes; the
    // original JS destructures `data` unguarded off the success branch.
    const { data: user } = (await this.props.getUser(
      this.props.client as ApolloClient<object>,
      this.props.userId
    )) as { data: object };
    this.setState({ user });
  };

  _buildFileViewLinks() {
    return map(this.props.approval.documents, (document) => {
      return (
        <div
          key={document.uri}
          className="document-item mb-2 p-3 rounded d-flex justify-content-between align-items-center"
        >
          <div className="d-flex align-items-center">
            <div className="document-icon">
              <FiFileText size={20} color="#0a2540" strokeWidth={1.5} />
            </div>
            <div>
              <div className="document-type ">{document.name}</div>
              <div className="document-name fw-semibold">
                {this._parseFileName(document)}
              </div>
            </div>
          </div>

          <div>
            {/* @ts-expect-error inert: Button requires `name`, omitted at runtime */}
            <Button
              color="action"
              size="sm"
              btnLabel="View"
              icon={{
                icon: <FiEye size={14} color={"#fffff"} />,
                position: "left",
              }}
              onClick={() => this.setState({ documentToView: document })}
            />
          </div>
        </div>
      );
    });
  }

  _parseFileName(file: ApprovalDocument) {
    const uriParts = file.uri.split("/");
    return `${uriParts[uriParts.length - 1]}`;
  }

  _onFileClose = () => {
    this.setState({
      documentToView: {},
    });
  };

  // Handle form submission - show deny modal if denying, otherwise submit directly
  handleFormSubmit = (values: ApprovalFormValues) => {
    if (values.approvalStatus === "DENIED") {
      this.setState({
        showDenyModal: true,
        pendingValues: values,
      });
    } else {
      void this._onSubmit(values);
    }
  };

  _onSubmit = async (values: ApprovalFormValues): Promise<boolean> => {
    if (this.state.isSubmitting) return false;
    this.setState({ isSubmitting: true });
    const isYellowPath =
      get(this.props.approval, "type.value", "") ===
      "DocumentAttestationReview";

    const approvalValues = {
      ...values,
      approvalStatus:
        values.approvalStatus === "RETRY" ? "DENIED" : values.approvalStatus,
    };
    if (!isYellowPath) {
      try {
        const result = await this.props.onSubmit({
          ...approvalValues,
        });

        const isSuccess = result.success;

        this.setState({
          showSuccessModal: isSuccess,
          pendingValues: values,
        });
        return isSuccess;
      } catch (error) {
        console.error("Error submitting approval:", error);
        return false;
      } finally {
        this.setState({ isSubmitting: false });
      }
      return false;
    }

    const handleAppeal = {
      APPROVED: approveYellowPathAppeal,
      DENIED: denyYellowPathAppeal,
      RETRY: retryYellowPathAppeal,
    };

    const yellowPathAppeal = this._buildYellowPathAppealPayload(values);
    const metadata = {
      appealReason: values.appealReason,
    };

    try {
      // The three appeal services have divergent second-arg shapes (YellowPathAppeal
      // vs RetryYellowPathDocumentUploadMutationVariables); the runtime selects one
      // by free-form approvalStatus and passes the same loosely-built payload through.
      await (
        handleAppeal[
          values.approvalStatus as keyof typeof handleAppeal
        ] as typeof approveYellowPathAppeal
      )(
        this.props.client as ApolloClient<object>,
        // the loosely-built payload (userId/appealReason may be absent on RETRY)
        // is passed through verbatim to whichever appeal service was selected
        yellowPathAppeal as unknown as Parameters<
          typeof approveYellowPathAppeal
        >[1]
      );

      const result = await this.props.onSubmit({ ...approvalValues, metadata });
      const isSuccess = result.success;

      this.setState({
        showSuccessModal: isSuccess,
        pendingValues: values,
      });
      return isSuccess;
    } catch (error) {
      toast.error(wrapError(error));
      return false;
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  _buildYellowPathAppealPayload(values: ApprovalFormValues) {
    const userId = get(this.props.approval, "entityIdentifier.id");
    const documentIds = this.props.approval.documents.map((d) => d.id);
    let yellowPathAppeal: {
      userId?: string;
      documentIds: string[];
      appealReason?: string;
    } = {
      userId,
      documentIds,
    };
    if (values.approvalStatus !== "RETRY") {
      yellowPathAppeal = {
        ...yellowPathAppeal,
        appealReason: values.appealReason,
      };
    }
    return yellowPathAppeal;
  }

  _getDocumentViewer = () => {
    if (isEmpty(this.state.documentToView)) {
      return null;
    }

    const uri = get(this.state.documentToView, "metadata.downloadUrl");

    if (this.state.documentToView.contentType === "application/pdf") {
      return (
        <PdfViewer
          documentUrl={uri}
          onClose={this._onFileClose}
          // @ts-expect-error inert: PdfViewer ignores `isPdf`; not in its props
          isPdf={false}
        />
      );
    } else {
      return (
        <Viewer
          images={[{ src: uri as string }]}
          visible={true}
          onClose={this._onFileClose}
          noClose={false}
          onMaskClick={this._onFileClose}
          zIndex={100000}
        />
      );
    }
  };

  _getDenyModal = () => {
    const { showDenyModal, pendingValues } = this.state;

    return (
      <Modal
        show={showDenyModal}
        onHide={() => this.setState({ showDenyModal: false })}
        centered
      >
        <Modal.Body>
          <IconMessageCard
            color="error"
            header={"Confirm Denial"}
            subheader={
              "Are you sure you want to deny this request? This action cannot be undone."
            }
            icon={<MdPriorityHigh color="b12121" stroke="#b12121" size={16} />}
          />
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "center", border: "none" }}>
          {/* @ts-expect-error inert: Button requires `name`, omitted at runtime */}
          <Button
            color="cancel"
            btnLabel="Cancel"
            onClick={() => this.setState({ showDenyModal: false })}
          >
            Cancel
          </Button>
          {/* @ts-expect-error inert: Button requires `name`, omitted at runtime */}
          <Button
            color="red"
            btnLabel="Yes, Deny"
            withArrow={true}
            onClick={async () => {
              this.setState({ showDenyModal: false });
              // pendingValues is set just before the deny modal opens; original
              // accessed it unguarded — non-null assertion preserves that throw
              if (await this._onSubmit(pendingValues!)) {
                this.props.push("/approval");
              }
            }}
          />
        </Modal.Footer>
      </Modal>
    );
  };

  _getSuccessModal = () => {
    const { pendingValues } = this.state;
    const approvalStatus = pendingValues?.approvalStatus || "APPROVED";
    const updatedBy = this.props.approval.updatedBy;

    let header;
    if (approvalStatus === "DENIED") {
      header = `Request was successfully denied by ${String(updatedBy)}`;
    } else if (approvalStatus === "RETRY") {
      header = `You have successfully requested new documents`;
    } else {
      header = `Request was successfully approved by ${String(updatedBy)}`;
    }

    return (
      <Modal
        show={this.state.showSuccessModal}
        onHide={() => this.setState({ showSuccessModal: false })}
        centered
      >
        <Modal.Body>
          <IconMessageCard
            header={header}
            icon={<FiCheck color="white" stroke="#60A4BF" size={16} />}
            actionText="Close"
            onClick={() => {
              this.setState({ showSuccessModal: false });
              this.props.push("/approval");
            }}
          />
        </Modal.Body>
      </Modal>
    );
  };

  getSubmissionDate() {
    const isNameChange =
      get(this.props.approval, "type.value", "") === "IraNameChange";

    const isAddressChange =
      get(this.props.approval, "type.value", "") === "IraAddressChange";

    let submissionDate = get(this.props.approval, "createdAt");
    if (isNameChange || isAddressChange) {
      const metadataItems = get(this.props.approval, "metadataItems", []);
      // getMetaValue returns `string | null`; submissionDate is `string |
      // undefined`. Runtime passes the value through verbatim to moment().
      submissionDate = this.getMetaValue(metadataItems, "Submission Date") as
        | string
        | undefined;
    }
    return submissionDate
      ? moment(submissionDate).format("dddd, MMMM Do YYYY")
      : null;
  }

  getMetaValue = (metadataItems: MetadataItem[], key: string) => {
    const item = find(
      metadataItems,
      (i) => i?.name?.trim().toLowerCase() === key.trim().toLowerCase()
    );
    return get(item, "value", null);
  };

  render() {
    const isNameChange =
      get(this.props.approval, "type.value", "") === "IraNameChange";

    const isAddressChange =
      get(this.props.approval, "type.value", "") === "IraAddressChange";

    const isYellowPath =
      get(this.props.approval, "type.value", "") ===
      "DocumentAttestationReview";

    const isUnfreezeRequest = [
      "EntityUnauthorizedAchUnfreeze",
      "EntityUnfreeze",
    ].includes(get(this.props.approval, "type.value", ""));

    const isBusinessMemberProfileUpdate =
      get(this.props.approval, "type.value", "") ===
      "EmployerBusinessMemberProfileUpdate";

    const isBusinessRegistrationUpdate =
      get(this.props.approval, "type.value", "") ===
      "EmployerBusinessRegistrationUpdate";

    const isUserKycProfileUpdate =
      get(this.props.approval, "type.value", "") === "UserKycProfileUpdate";

    const submissionDate = this.getSubmissionDate();

    const submissionMoment = moment(submissionDate, moment.ISO_8601, true);

    const daysSinceSubmission = submissionMoment.isValid()
      ? moment().diff(submissionMoment, "days")
      : 0;

    let ApprovalComponent;

    if (isNameChange) {
      ApprovalComponent = (
        <NameChangeApproval
          user={this.state.user}
          approval={this.props.approval}
        />
      );
    } else if (isAddressChange) {
      ApprovalComponent = (
        <AddressApproval
          // state.user is the loosely-typed action payload; approval is the
          // local `get`-shaped interface. Both are passed through verbatim to
          // the strictly-typed (User/ApprovalItem) child props.
          user={
            this.state.user as unknown as React.ComponentProps<
              typeof AddressApproval
            >["user"]
          }
          approval={
            this.props.approval as unknown as React.ComponentProps<
              typeof AddressApproval
            >["approval"]
          }
        />
      );
    } else if (isYellowPath) {
      ApprovalComponent = (
        <YellowPathApproval
          // loosely-typed state.user / local approval passed through verbatim
          // to the strictly-typed (User/ApprovalItem) child props
          user={
            this.state.user as unknown as React.ComponentProps<
              typeof YellowPathApproval
            >["user"]
          }
          approval={
            this.props.approval as unknown as React.ComponentProps<
              typeof YellowPathApproval
            >["approval"]
          }
        />
      );
    } else if (isUnfreezeRequest) {
      ApprovalComponent = (
        <UnfreezeRequestApproval
          // local approval passed through verbatim to the child's own
          // (structurally-different) Approval prop type
          approval={
            this.props.approval as unknown as React.ComponentProps<
              typeof UnfreezeRequestApproval
            >["approval"]
          }
        />
      );
    } else if (isBusinessMemberProfileUpdate) {
      ApprovalComponent = (
        <BusinessMemberProfileUpdateApproval
          // local approval passed through verbatim to the strictly-typed
          // (ApprovalItem) child prop
          approval={
            this.props.approval as unknown as React.ComponentProps<
              typeof BusinessMemberProfileUpdateApproval
            >["approval"]
          }
        />
      );
    } else if (isBusinessRegistrationUpdate) {
      ApprovalComponent = (
        <BusinessRegistrationUpdateApproval approval={this.props.approval} />
      );
    } else if (isUserKycProfileUpdate) {
      ApprovalComponent = (
        <UserKycProfileUpdateApproval approval={this.props.approval} />
      );
    }

    return (
      <Container fluid className="approval-card">
        {this.props.isFetching && <IconSpinner centered />}
        {!this.props.isFetching && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p className="page-title">
                  {get(this.props.approval, "type.label", "Approval Item")}
                </p>
                <p className="page-subtext">Review and approve request</p>
              </div>
            </div>

            <Row>
              <Col lg={8}>
                {/* Approval Component */}
                {ApprovalComponent}

                {/* Supporting Documents */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    Supporting Documents
                  </Card.Header>
                  <Card.Body>{this._buildFileViewLinks()}</Card.Body>
                </Card>
              </Col>

              {/*  Review & Approve */}
              <Col lg={4}>
                <div className="sticky-top" style={{ top: "20px" }}>
                  <Card>
                    <Card.Header className="bg-light">
                      {this.props.approval.status === "APPROVED" ||
                      this.props.approval.status === "DENIED"
                        ? "Review Status"
                        : "Review & Approve"}
                    </Card.Header>

                    <Card.Body>
                      {this.props.approval.status === "APPROVED" ||
                      this.props.approval.status === "DENIED" ? (
                        <div className="text-center py-4">
                          <div
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              backgroundColor:
                                this.props.approval.status === "APPROVED"
                                  ? "#d4edda"
                                  : "#f8d7da",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 16px",
                            }}
                          >
                            {this.props.approval.status === "APPROVED" ? (
                              <FiCheck size={32} color="#28a745" />
                            ) : (
                              <MdPriorityHigh size={32} color="#dc3545" />
                            )}
                          </div>
                          <h5
                            style={{
                              color:
                                this.props.approval.status === "APPROVED"
                                  ? "#28a745"
                                  : "#dc3545",
                              marginBottom: 16,
                            }}
                          >
                            {this.props.approval.status === "APPROVED"
                              ? "Approved"
                              : "Denied"}
                          </h5>
                          {this.props.approval.updatedBy && (
                            <p
                              className="mb-1 text-muted"
                              style={{ fontSize: 14 }}
                            >
                              By: {this.props.approval.updatedBy}
                            </p>
                          )}
                          {this.props.approval.updatedAt && (
                            <p
                              className="mb-0 text-muted"
                              style={{ fontSize: 14 }}
                            >
                              {moment(this.props.approval.updatedAt).format(
                                "MMM D, YYYY h:mm A"
                              )}
                            </p>
                          )}
                        </div>
                      ) : (
                        <Formik
                          validateOnChange={false}
                          validationSchema={schema}
                          onSubmit={this.handleFormSubmit}
                          enableReinitialize={false}
                          initialValues={{
                            ...this.state,
                            isYellowPath,
                          }}
                        >
                          {({
                            handleSubmit,
                            handleChange,
                            values,
                            touched,
                            errors,
                            handleBlur,
                          }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                              <ScrollToFieldError />

                              <Form.Group className="mb-3">
                                <Form.Label>Approval Status</Form.Label>
                                <Form.Select
                                  name="approvalStatus"
                                  value={values.approvalStatus}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  isInvalid={
                                    touched.approvalStatus &&
                                    !!errors.approvalStatus
                                  }
                                  isValid={
                                    touched.approvalStatus &&
                                    !errors.approvalStatus
                                  }
                                >
                                  <option value="" disabled>
                                    Select approval status
                                  </option>
                                  {map(approvalOptions, (opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {errors.approvalStatus}
                                </Form.Control.Feedback>
                              </Form.Group>

                              <Form.Group className="mb-3">
                                <Form.Label>Comments</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  name="comments"
                                  placeholder="Add comments or reasons for your decision..."
                                  value={values.comments}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  isInvalid={
                                    touched.comments && !!errors.comments
                                  }
                                  isValid={touched.comments && !errors.comments}
                                />
                              </Form.Group>

                              {isYellowPath &&
                                (values.approvalStatus === "APPROVED" ||
                                  values.approvalStatus === "DENIED") && (
                                  <Form.Group className="mb-3">
                                    <Form.Label>Appeal Reason</Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      name="appealReason"
                                      value={values.appealReason}
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      isInvalid={
                                        touched.appealReason &&
                                        !!errors.appealReason
                                      }
                                      isValid={
                                        touched.appealReason &&
                                        !errors.appealReason
                                      }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errors.appealReason}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                )}

                              {values.approvalStatus === "RETRY" && (
                                <Form.Group className="mb-3">
                                  <Form.Label>Retry Reason</Form.Label>
                                  <Form.Select
                                    name="reason"
                                    value={values.reason}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    isInvalid={
                                      touched.reason && !!errors.reason
                                    }
                                    isValid={touched.reason && !errors.reason}
                                  >
                                    <option value="" disabled>
                                      Select Reason
                                    </option>
                                    {map(
                                      this.props.approval.allReasons,
                                      (reason) => (
                                        <option key={reason} value={reason}>
                                          {reason}
                                        </option>
                                      )
                                    )}
                                  </Form.Select>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.reason}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              )}

                              <div>
                                {this.props.isSelfApproval && (
                                  <SelfApprovalAlert />
                                )}
                                {this.props.error && (
                                  <Alert type="error" msg={this.props.error} />
                                )}
                                <Button
                                  name="submit"
                                  withArrow={true}
                                  btnLabel="Submit"
                                  loading={this.state.isSubmitting}
                                  disabled={this.props.isSelfApproval}
                                />
                              </div>
                            </Form>
                          )}
                        </Formik>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Request Timeline */}
                  <Card className="mt-4">
                    <Card.Header className="bg-light">
                      Request Timeline
                    </Card.Header>
                    <Card.Body>
                      <div className="timeline">
                        <div className="timeline-item">
                          <div className="timeline-marker">
                            <FiCalendar
                              size={16}
                              color="#0a2540"
                              stroke="#0a2540"
                            />
                          </div>
                          <div className="timeline-content">
                            <p className="account-info-label">
                              Request Submitted
                            </p>
                            <p className="account-info-label timeline-date">
                              {submissionDate}
                            </p>
                            {this.props.approval.createdBy && (
                              <p className="account-info-label timeline-date">
                                By: {this.props.approval.createdBy}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="timeline-item">
                          <div className="timeline-marker">
                            <FiClock
                              size={16}
                              color="#0a2540"
                              stroke="#0a2540"
                            />
                          </div>
                          <div className="timeline-content">
                            <p className="account-info-label">Request Age</p>
                            <p className="account-info-label timeline-age">
                              {daysSinceSubmission} days
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>

            {this._getDocumentViewer()}
            {this._getDenyModal()}
            {this._getSuccessModal()}
          </>
        )}
      </Container>
    );
  }
}

export default connector(withApollo<Props>(ApprovalDocumentItem));
